import { useEffect, useState } from "react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { getPremiumStatus, startPremiumCheckout, cancelPremium, clearPremiumCache } from "@/lib/premiumApi";
import { startGoogleLogin } from "@/lib/userApi";
import { toast } from "sonner";

const FREE_PERKS = [
  "Unlimited trend browsing",
  "Higher / Lower game",
  "Weekly Monday Index newsletter",
  "Signals radar (Reddit · HN · GitHub)",
  "AI opportunity scores on every trend",
];

const PREMIUM_PERKS = [
  "Everything in Free",
  "AI Business Builder — unlimited business plans on any trend",
  "100% ad-free experience",
  "Premium badge on your account",
  "Priority email support",
];

export default function PremiumPage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPremiumStatus().then(setStatus);
  }, []);

  const upgrade = async () => {
    if (!status?.signed_in) {
      startGoogleLogin();
      return;
    }
    setBusy(true);
    try {
      const { url } = await startPremiumCheckout();
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed — try again");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel Premium? It stays active until the end of the paid period.")) return;
    setBusy(true);
    try {
      await cancelPremium();
      clearPremiumCache();
      toast.success("Subscription set to cancel at period end.");
      getPremiumStatus().then(setStatus);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed");
    }
    setBusy(false);
  };

  return (
    <div data-testid="premium-page" className="bg-white text-[#0a0a0a]">
      <Seo
        title="Universtrend Premium — AI Business Builder, ad-free, $4.99/mo"
        description="Unlock the AI Business Builder on every trend, remove all ads, and support the index. $4.99/month, cancel anytime."
      />
      <Header />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16">
          <div className="overline text-[#52525b] mb-4">Membership</div>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl lg:text-7xl max-w-4xl">
            Every trend hides a business.<br />
            <span className="text-[#ff3b30]">Premium finds it for you.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[#52525b] text-base lg:text-lg">
            The AI Business Builder turns any search trend into a launch-ready plan — name, products,
            pricing, marketing and first steps. Plus a completely ad-free reading experience.
          </p>

          {status?.premium && (
            <div data-testid="premium-active-banner" className="mt-8 border border-black bg-[#f4f4f5] p-6 max-w-xl">
              <div className="overline text-[#0a7d33]">Premium active ✓</div>
              <div className="font-mono text-xs text-[#52525b] mt-2">
                {status.cancel_at_period_end
                  ? `Cancels at period end · access until ${String(status.premium_until || "").slice(0, 10)}`
                  : `Renews monthly · paid through ${String(status.premium_until || "").slice(0, 10)}`}
              </div>
              {!status.cancel_at_period_end && (
                <button
                  data-testid="premium-cancel-btn"
                  onClick={cancel}
                  disabled={busy}
                  className="mt-4 border border-black px-5 py-2 overline hover:bg-black hover:text-white disabled:opacity-50"
                >
                  Cancel subscription
                </button>
              )}
            </div>
          )}

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 border border-black max-w-4xl">
            <div className="p-8 border-b lg:border-b-0 lg:border-r border-black">
              <div className="overline text-[#52525b]">Free</div>
              <div className="font-display font-extrabold text-5xl tracking-tighter mt-2">$0</div>
              <div className="font-mono text-xs text-[#52525b]">forever</div>
              <ul className="mt-6 space-y-3 text-sm">
                {FREE_PERKS.map((p) => (
                  <li key={p}><span className="text-[#52525b] mr-2">✓</span>{p}</li>
                ))}
              </ul>
            </div>
            <div className="p-8 bg-[#0a0a0a] text-white relative">
              <div className="absolute top-0 right-0 bg-[#ff3b30] text-white overline px-3 py-1">Most popular</div>
              <div className="overline text-[#a1a1aa]">Premium</div>
              <div className="font-display font-extrabold text-5xl tracking-tighter mt-2">
                $4.99<span className="text-xl text-[#a1a1aa]">/mo</span>
              </div>
              <div className="font-mono text-xs text-[#a1a1aa]">cancel anytime · Stripe secure</div>
              <ul className="mt-6 space-y-3 text-sm text-[#d4d4d8]">
                {PREMIUM_PERKS.map((p) => (
                  <li key={p}><span className="text-[#ff3b30] mr-2">✓</span>{p}</li>
                ))}
              </ul>
              {!status?.premium && (
                <button
                  data-testid="premium-upgrade-btn"
                  onClick={upgrade}
                  disabled={busy}
                  className="hard-shadow mt-8 w-full border border-white bg-[#ff3b30] text-white px-6 py-4 overline disabled:opacity-50"
                >
                  {busy ? "Redirecting to Stripe…" : status?.signed_in ? "Go Premium →" : "Sign in with Google to start →"}
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 font-mono text-xs text-[#52525b] max-w-xl">
            Payments processed by Stripe. Your subscription supports the free index —
            and removes every ad from your reading experience.
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
